import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Customized } from 'recharts';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ChartDataPoint, NewsEvent } from '../../types/game';
import { StockCategoryFilter } from '../../types/stockCategories';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';
import { useGameStart } from '../../contexts/GameStartContext';
import { ChartSummaryBanner } from './ChartSummaryBanner';
import NewsBubbleTooltip from './NewsBubbleTooltip';

interface StockChartProps {
  data?: ChartDataPoint[];
  className?: string;
  loading?: boolean;
  backgroundClassName?: string;
  backgroundStyle?: React.CSSProperties;
  showPrice?: boolean;
  showDate?: boolean;
  category?: StockCategoryFilter;
  isReplayMode?: boolean;
  newsEvents?: NewsEvent[] | null;
  visibleNewsCount?: number;
}

type TimePeriod = '10Y' | '5Y' | '3Y' | '1Y' | '6M' | '1M';

type ProcessedChartDataPoint = ChartDataPoint & { index: number };

// Validation function for chart data format
const validateChartData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!Array.isArray(data)) {
    errors.push('Chart data is not an array');
    return { isValid: false, errors };
  }
  
  if (data.length === 0) {
    errors.push('Chart data array is empty');
    return { isValid: false, errors };
  }
  
  // Validate each data point
  data.forEach((point, index) => {
    if (!point || typeof point !== 'object') {
      errors.push(`Data point at index ${index} is not an object`);
      return;
    }
    
    if (!point.hasOwnProperty('date') || typeof point.date !== 'string') {
      errors.push(`Data point at index ${index} missing or invalid 'date' field`);
    }
    
    if (!point.hasOwnProperty('price') || typeof point.price !== 'number' || isNaN(point.price)) {
      errors.push(`Data point at index ${index} missing or invalid 'price' field`);
    }
    
    // Validate date format
    if (point.date && typeof point.date === 'string') {
      const dateObj = new Date(point.date);
      if (isNaN(dateObj.getTime())) {
        errors.push(`Data point at index ${index} has invalid date format: ${point.date}`);
      }
    }
    
    // Validate price is positive
    if (typeof point.price === 'number' && !isNaN(point.price) && point.price <= 0) {
      errors.push(`Data point at index ${index} has non-positive price: ${point.price}`);
    }
  });
  
  return { isValid: errors.length === 0, errors };
};

const StockChartComponent: React.FC<StockChartProps> = ({
  data = [],
  className,
  loading = false,
  backgroundClassName,
  backgroundStyle,
  showPrice = true,
  showDate = true,
  category,
  isReplayMode = false,
  newsEvents,
  visibleNewsCount = 0,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('10Y');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const layoutVariant = useLayoutVariant();
  const isMobile = layoutVariant.type === 'mobile';
  const isStreamerDesktop = layoutVariant.isStreamerMode;

  // Stabilize period selection handler
  const handlePeriodChange = useCallback((period: TimePeriod) => {
    setSelectedPeriod(period);
  }, []);

  // Comprehensive debug logging for chart data
  console.log('📊 [StockChart] Component rendering with props:', {
    dataLength: data?.length || 0,
    loading,
    className,
    hasData: Array.isArray(data) && data.length > 0,
    sampleData: data?.slice(0, 3), // Show first 3 items for debugging
    dataType: typeof data,
    isArray: Array.isArray(data),
    timestamp: new Date().toISOString()
  });

  // Validate chart data format and structure
  const validation = validateChartData(data);
  console.log('📊 [StockChart] Data validation result:', validation);
  
  if (!validation.isValid) {
    console.error('📊 [StockChart] Chart data validation failed:', validation.errors);
    validation.errors.forEach(error => console.error(`  - ${error}`));
  } else {
    console.log('📊 [StockChart] Chart data validation passed ✅');
  }

  // Memoize static data to prevent recreation
  const timePeriods = useMemo<TimePeriod[]>(() => ['1M', '6M', '1Y', '3Y', '5Y', '10Y'], []);

  // Memoize formatting functions to maintain reference equality
  const formatDateLabel = useCallback((dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = date.toLocaleString('en-US', { day: '2-digit' });
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  }, []);

  const formatSignedCurrency = useCallback((value: number): string => {
    if (value < 0) {
      return `-$${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `+$${Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  // Filter data based on selected period and calculate performance
  const { chartData, lineColor, processingErrors, yAxisDomain, yAxisTicks } = useMemo((): {
    chartData: ProcessedChartDataPoint[];
    lineColor: string;
    processingErrors: string[];
    yAxisDomain: [number, number | 'auto' | 'dataMax'];
    yAxisTicks: number[];
  } => {
    console.log('📊 [StockChart] Starting data processing in useMemo:', {
      timestamp: new Date().toISOString(),
      selectedPeriod,
      inputDataLength: data?.length || 0,
      inputDataType: typeof data,
      isInputArray: Array.isArray(data)
    });

    const errors: string[] = [];
    
    // Validate input data before processing
    if (!Array.isArray(data)) {
      errors.push('Input data is not an array');
      console.error('📊 [StockChart] Input data validation failed: not an array');
      return {
        chartData: [],
        lineColor: 'hsl(var(--primary))',
        processingErrors: errors,
        yAxisDomain: [0, 'auto'],
        yAxisTicks: []
      };
    }

    // Use the actual data passed from the parent component
    const baseData = data.length > 0 ? data : [];
    
    console.log('📊 [StockChart] Base data prepared:', {
      originalDataLength: data.length,
      baseDataLength: baseData.length,
      sampleBaseData: baseData.slice(0, 2),
      dataDateRange: baseData.length > 0 ? {
        first: baseData[0]?.date,
        last: baseData[baseData.length - 1]?.date
      } : null
    });

    // Validate each data point in base data
    const validDataPoints: ChartDataPoint[] = [];
    baseData.forEach((point, index) => {
      if (!point || typeof point !== 'object') {
        errors.push(`Invalid data point at index ${index}: not an object`);
        return;
      }
      
      if (typeof point.date !== 'string' || typeof point.price !== 'number' || isNaN(point.price)) {
        errors.push(`Invalid data point at index ${index}: invalid date or price`);
        return;
      }
      
      // Validate date can be parsed
      const dateObj = new Date(point.date);
      if (isNaN(dateObj.getTime())) {
        errors.push(`Invalid data point at index ${index}: unparseable date ${point.date}`);
        return;
      }
      
      validDataPoints.push(point);
    });

    console.log('📊 [StockChart] Data validation completed:', {
      originalPoints: baseData.length,
      validPoints: validDataPoints.length,
      invalidPoints: baseData.length - validDataPoints.length,
      errors: errors.length > 0 ? errors : 'None'
    });

    // Filter data based on selected period
    let filteredData: ChartDataPoint[];
    const periodMapping = {
      '1M': Math.min(30, validDataPoints.length),   // Approx. last month of trading days
      '6M': Math.min(130, validDataPoints.length),  // Approx. last 6 months
      '1Y': Math.min(260, validDataPoints.length),  // Approx. last trading year
      '3Y': Math.min(780, validDataPoints.length),  // Approx. last 3 trading years
      '5Y': Math.min(1300, validDataPoints.length), // Approx. last 5 trading years
      '10Y': validDataPoints.length // All data (10 years provided by API)
    };

    const pointsToTake = periodMapping[selectedPeriod] || validDataPoints.length;
    filteredData = validDataPoints.slice(-pointsToTake);

    console.log('📊 [StockChart] Period filtering applied:', {
      selectedPeriod,
      pointsToTake,
      filteredDataLength: filteredData.length,
      dateRange: filteredData.length > 0 ? {
        first: filteredData[0]?.date,
        last: filteredData[filteredData.length - 1]?.date
      } : null
    });

    // Calculate color based on performance over the selected period
    let color = 'hsl(var(--primary))'; // Default color
    let performanceData = null;
    
    if (filteredData.length >= 2) {
      const firstPrice = filteredData[0].price;
      const lastPrice = filteredData[filteredData.length - 1].price;
      const priceChange = lastPrice - firstPrice;
      const percentageChange = (priceChange / firstPrice) * 100;

      performanceData = {
        firstPrice,
        lastPrice,
        priceChange,
        percentageChange
      };

      if (priceChange > 0) {
        color = '#22c55e'; // Green for positive performance
      } else if (priceChange < 0) {
        color = '#ef4444'; // Red for negative performance
      }
      // If priceChange === 0, keep default color
    }

    console.log('📊 [StockChart] Performance calculation completed:', {
      hasPerformanceData: performanceData !== null,
      performanceData,
      lineColor: color,
      finalDataLength: filteredData.length
    });

    const minPrice = filteredData.length > 0
      ? filteredData.reduce((min, point) => Math.min(min, point.price), Number.POSITIVE_INFINITY)
      : 0;
    const maxPrice = filteredData.length > 0
      ? filteredData.reduce((max, point) => Math.max(max, point.price), Number.NEGATIVE_INFINITY)
      : 0;
    const priceRange = maxPrice - minPrice;
    const padding = Math.max(priceRange * 0.1, maxPrice * 0.01);
    const yAxisMin = Math.max(0, Math.floor(minPrice - padding));
    const yAxisMax = Math.ceil(maxPrice + padding);
    const yAxisDomain: [number, number | 'auto' | 'dataMax'] = [yAxisMin, yAxisMax];

    // Generate exactly 5 equidistant Y axis ticks
    const yAxisTicks = Array.from({ length: 5 }, (_, i) => yAxisMin + (i * (yAxisMax - yAxisMin)) / 4);

    const chartDataWithIndex = filteredData.map((point, index) => ({
      ...point,
      index,
    }));

    console.log('📊 [StockChart] Final processed chart data summary:', {
      filteredDataLength: filteredData.length,
      lineColor: color,
      sampleFilteredData: filteredData.slice(0, 2),
      processingErrors: errors.length,
      yAxisDomain,
      timestamp: new Date().toISOString()
    });

    return {
      chartData: chartDataWithIndex,
      lineColor: color,
      processingErrors: errors,
      yAxisDomain,
      yAxisTicks
    };
  }, [data, selectedPeriod]);

  const formatAxisCurrency = useCallback((value: number): string => {
    const maxVal = typeof yAxisDomain[1] === 'number' ? yAxisDomain[1] : value;
    const fractionDigits = maxVal <= 10 ? 2 : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }, [yAxisDomain]);

  const axisTicks = useMemo(() => {
    if (chartData.length === 0) {
      return [] as number[];
    }

    // Use 4 ticks on mobile, 7 on desktop
    const desiredTickCount = isMobile
      ? Math.min(chartData.length, 4)
      : (chartData.length < 7 ? chartData.length : 7);

    if (desiredTickCount === 0) {
      return [] as number[];
    }

    if (chartData.length <= desiredTickCount) {
      return chartData.map(point => point.index);
    }

    const step = (chartData.length - 1) / (desiredTickCount - 1);
    const ticksSet = new Set<number>();

    for (let i = 0; i < desiredTickCount; i++) {
      const tickIndex = Math.round(i * step);
      ticksSet.add(tickIndex);
    }

    // Ensure start and end ticks are always present
    ticksSet.add(0);
    ticksSet.add(chartData.length - 1);

    // If rounding caused duplicates, fill remaining slots sequentially
    for (let i = 0; ticksSet.size < desiredTickCount && i < chartData.length; i++) {
      ticksSet.add(i);
    }

    return Array.from(ticksSet).sort((a, b) => a - b);
  }, [chartData, isMobile]);

  // Lookup table: date string -> local index in the current filtered view.
  // Date-based mapping avoids fragile offset math when `chartData` is a slice
  // of `data` whose length can diverge (e.g. invalid points dropped in validation).
  const dateToLocalIndex = useMemo(() => {
    const map = new Map<string, number>();
    chartData.forEach((point, idx) => map.set(point.date, idx));
    return map;
  }, [chartData]);

  // Map news events to local chart indices for the current time period
  const visibleNewsEvents = useMemo(() => {
    if (!newsEvents || newsEvents.length === 0 || visibleNewsCount <= 0 || chartData.length === 0) {
      return [];
    }

    // Sort events by absolute pctChange descending (largest first) to gate by visibleNewsCount
    const sortedByMagnitude = [...newsEvents].sort(
      (a, b) => Math.abs(b.pctChange) - Math.abs(a.pctChange)
    );

    // Take only the number of events allowed by visibleNewsCount
    const gatedEvents = sortedByMagnitude.slice(0, visibleNewsCount);

    return gatedEvents
      .map((event) => {
        const localIndex = dateToLocalIndex.get(event.date);
        if (localIndex === undefined) {
          return null; // Event date falls outside the current time period view
        }
        // Use the current chart price for Y positioning rather than the stored
        // priceAtEvent — chart_data is refreshed independently and may be
        // retroactively split-adjusted, which would float the star off the line.
        const linePrice = chartData[localIndex].price;
        return { ...event, localIndex, priceAtEvent: linePrice };
      })
      .filter(Boolean) as (NewsEvent & { localIndex: number })[];
  }, [newsEvents, visibleNewsCount, chartData, dateToLocalIndex]);

  // Memoize custom tick component for slanted labels to prevent recreation
  const CustomXAxisTick = useCallback((props: any) => {
    const { x, y, payload } = props;
    const parsedIndex = Number(payload.value);
    const clampedIndex = Number.isFinite(parsedIndex)
      ? Math.min(Math.max(Math.round(parsedIndex), 0), Math.max(chartData.length - 1, 0))
      : 0;
    const rawDate = chartData[clampedIndex]?.date ?? '';
    const displayText = formatDateLabel(rawDate || String(payload.value));
    const adjustedX = x - 15;
    const adjustedY = y + 15;

    const fontSize = isMobile ? 11 : 14;

    return (
      <g transform={`translate(${adjustedX},${adjustedY})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="currentColor"
          fontSize={fontSize}
          transform="rotate(-30)"
          className="text-black dark:text-white"
        >
          {displayText}
        </text>
      </g>
    );
  }, [chartData, formatDateLabel, isMobile]);

  // Build a Set of local indices near news events so we can suppress the price tooltip there
  const newsEventIndices = useMemo(() => {
    const indices = new Set<number>();
    for (const ev of visibleNewsEvents) {
      // Suppress tooltip within +-3 data points of the star
      for (let d = -3; d <= 3; d++) {
        indices.add(ev.localIndex + d);
      }
    }
    return indices;
  }, [visibleNewsEvents]);

  const CustomTooltip = useCallback(({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const parsedIndex = Number(label);

      // Hide price tooltip near news event stars to prevent flicker
      if (newsEventIndices.has(Math.round(parsedIndex))) {
        return null;
      }

      const data = payload[0];
      const clampedIndex = Number.isFinite(parsedIndex)
        ? Math.min(Math.max(Math.round(parsedIndex), 0), Math.max(chartData.length - 1, 0))
        : 0;
      const rawDate = (data?.payload?.date as string) ?? chartData[clampedIndex]?.date ?? '';
      return (
        <div className="bg-popover border border-border p-3 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-popover-foreground">
            {showDate ? formatDateLabel(rawDate || String(label)) : '???'}
          </p>
          {showPrice && (
            <p className="text-sm text-primary">
              Price: {formatSignedCurrency(Number(data.value))}
            </p>
          )}
        </div>
      );
    }
    return null;
  }, [chartData, formatDateLabel, formatSignedCurrency, showDate, showPrice, newsEventIndices]);

  const CustomYAxisTick = useCallback(({ x, y, payload }: any) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={isMobile ? -8 : -x}
          y={0}
          dy={4}
          textAnchor={isMobile ? "end" : "start"}
          fill="currentColor"
          fontSize={isMobile ? 12 : 14}
          className="text-black dark:text-white"
        >
          {showPrice ? formatAxisCurrency(Number(payload.value)) : '???'}
        </text>
      </g>
    );
  }, [formatAxisCurrency, isMobile, showPrice]);

  const latestPrice = chartData.length > 0 ? chartData[chartData.length - 1].price : null;
  const baseChartHeights = 'h-[14rem] sm:h-[16rem] lg:h-[23.63rem] xl:h-[26.69rem] 2xl:h-[31.5rem] lg:min-h-[23.63rem] xl:min-h-[26.69rem] 2xl:min-h-[31.5rem]';
  const streamerChartHeights = 'h-[14rem] sm:h-[16rem] lg:h-[18rem] xl:h-[20rem] 2xl:h-[22rem] lg:min-h-[24rem] xl:min-h-[26rem] 2xl:min-h-[28rem]';
  const chartContainerClasses = cn(
    isStreamerDesktop ? streamerChartHeights : baseChartHeights,
    'w-full',
    isMobile ? 'mt-6' : 'mt-12'
  );

  // Final render logging
  console.log('📊 [StockChart] About to render component:', {
    willRenderChart: chartData.length > 0 && processingErrors.length === 0,
    chartDataLength: chartData.length,
    processingErrorsCount: processingErrors.length,
    loading,
    selectedPeriod,
    timestamp: new Date().toISOString()
  });

  const { goBack } = useGameStart();
  const navigate = useNavigate();

  return (
    <div data-tutorial-id="price-chart" className={cn("", className)}>
      {/* Back button — navigates to today's game in replay mode, shows overlay otherwise */}
      <button
        onClick={isReplayMode ? () => navigate(`/${category || ''}`) : goBack}
        className="flex items-center gap-1 text-[clamp(0.75rem,1.4vw,0.875rem)] text-muted-foreground hover:text-foreground transition-colors mb-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      {/* Header */}
      {!isStreamerDesktop && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-[clamp(1.25rem,2.5vw,1.625rem)] font-normal" style={{ color: 'var(--clue-text-primary)' }}>Price Chart</h3>

          {/* Time Period Filter */}
          <div className="flex items-center">
            {timePeriods.map((period, index) => (
              <React.Fragment key={period}>
                <button
                  onClick={() => handlePeriodChange(period)}
                  className={cn(
                    "bg-transparent border-none cursor-pointer transition-colors",
                    isMobile ? "text-[clamp(0.625rem,1.2vw,0.75rem)] px-1" : "text-[clamp(0.75rem,1.4vw,0.875rem)] px-1.5",
                    selectedPeriod === period
                      ? "text-blue-500 font-bold underline"
                      : "text-muted-foreground hover:text-foreground font-medium"
                  )}
                >
                  {period}
                </button>
                {index < timePeriods.length - 1 && (
                  <span className="text-muted-foreground select-none">|</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Summary banner + inline time periods in streamer mode */}
      <div className={cn(isStreamerDesktop && 'flex items-center justify-between')}>
        <ChartSummaryBanner
          latestPrice={latestPrice}
          chartData={chartData}
          selectedPeriod={selectedPeriod}
          lineColor={lineColor}
          showPrice={showPrice}
          category={category}
        />
        {isStreamerDesktop && (
          <div className="flex items-center">
            {timePeriods.map((period, index) => (
              <React.Fragment key={period}>
                <button
                  onClick={() => handlePeriodChange(period)}
                  className={cn(
                    "bg-transparent border-none cursor-pointer transition-colors text-[clamp(0.625rem,1.2vw,0.75rem)] px-1",
                    selectedPeriod === period
                      ? "text-blue-500 font-bold underline"
                      : "text-muted-foreground hover:text-foreground font-medium"
                  )}
                >
                  {period}
                </button>
                {index < timePeriods.length - 1 && (
                  <span className="text-muted-foreground select-none">|</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Chart Container */}
      <div className="relative" ref={chartContainerRef}>
        <div
          className={cn('pt-6', chartContainerClasses, backgroundClassName)}
          style={backgroundStyle}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-muted-foreground">Loading chart data...</div>
            </div>
        ) : processingErrors.length > 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <div className="text-red-500 font-medium">Chart data processing error</div>
            <div className="text-sm text-muted-foreground text-center max-w-md">
              {processingErrors.length === 1 
                ? processingErrors[0] 
                : `${processingErrors.length} data validation errors detected`}
            </div>
            {process.env.NODE_ENV === 'development' && (
              <details className="text-xs text-muted-foreground mt-2">
                <summary className="cursor-pointer">Show details</summary>
                <ul className="mt-1 space-y-1">
                  {processingErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <div className="text-muted-foreground">No chart data available</div>
            <div className="text-sm text-muted-foreground">
              {data.length === 0 ? 'No data provided' : 'All data points were invalid'}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" className={backgroundClassName ? 'relative z-10' : undefined}>
            <AreaChart
              data={chartData}
              margin={isMobile ? {
                top: 5,
                right: 0,
                left: 0,
                bottom: 28,
              } : {
                top: 5,
                right: 30,
                left: 20,
                bottom: 48,
              }}
            >
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="index"
                type="number"
                domain={[0, Math.max(chartData.length - 1, 0)]}
                tick={<CustomXAxisTick />}
                height={60}
                allowDecimals={false}
                minTickGap={12}
                scale="linear"
                ticks={axisTicks}
                padding={{ left: 0, right: 0 }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={<CustomYAxisTick />}
                className="text-muted-foreground"
                domain={yAxisDomain}
                ticks={yAxisTicks}
                width={isMobile ? 48 : 40}
              />
              {(showPrice || showDate) && <Tooltip content={<CustomTooltip />} />}
              <Area
                type="linear"
                dataKey="price"
                stroke={lineColor}
                strokeWidth={2}
                fill="url(#areaGradient)"
                fillOpacity={1}
                dot={false}
                activeDot={{ r: 4, fill: lineColor }}
                isAnimationActive={false}
              />
              {/* News event star markers */}
              {visibleNewsEvents.length > 0 && (
                <Customized
                  component={(props: any) => {
                    const { xAxisMap, yAxisMap } = props;
                    const xAxis = xAxisMap && Object.values(xAxisMap)[0] as any;
                    const yAxis = yAxisMap && Object.values(yAxisMap)[0] as any;
                    if (!xAxis?.scale || !yAxis?.scale) return null;
                    return (
                      <g className="news-bubbles">
                        {visibleNewsEvents.map((event, idx) => {
                          const cx = xAxis.scale(event.localIndex);
                          const cy = yAxis.scale(event.priceAtEvent);
                          if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
                          return (
                            <NewsBubbleTooltip
                              key={`news-${event.chartIndex}`}
                              event={event}
                              cx={cx}
                              cy={cy}
                              bubbleIndex={idx}
                              visible={true}
                              chartContainerRef={chartContainerRef}
                            />
                          );
                        })}
                      </g>
                    );
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
        </div>
      </div>
    </div>
  );
};

/**
 * Custom comparison function for React.memo to prevent unnecessary re-renders
 * Only re-render if props have actually changed
 */
const arePropsEqual = (
  prevProps: StockChartProps,
  nextProps: StockChartProps
): boolean => {
  // Compare primitive props
  if (
    prevProps.loading !== nextProps.loading ||
    prevProps.className !== nextProps.className ||
    prevProps.backgroundClassName !== nextProps.backgroundClassName ||
    prevProps.showPrice !== nextProps.showPrice ||
    prevProps.showDate !== nextProps.showDate ||
    prevProps.category !== nextProps.category ||
    prevProps.isReplayMode !== nextProps.isReplayMode ||
    prevProps.visibleNewsCount !== nextProps.visibleNewsCount ||
    prevProps.newsEvents !== nextProps.newsEvents
  ) {
    return false;
  }

  // Compare backgroundStyle object
  if (prevProps.backgroundStyle !== nextProps.backgroundStyle) {
    return false;
  }

  // Compare data array - use reference equality for performance
  // since data should be memoized at a higher level
  if (prevProps.data !== nextProps.data) {
    // If both are null/undefined, they're equal
    if (!prevProps.data && !nextProps.data) {
      return true;
    }
    // If one is null/undefined and the other isn't, they're different
    if (!prevProps.data || !nextProps.data) {
      return false;
    }
    // If lengths are different, they're different
    if (prevProps.data.length !== nextProps.data.length) {
      return false;
    }
    // For performance, rely on reference equality since data should be memoized
    return false;
  }

  return true;
};

/**
 * Memoized StockChart component to prevent unnecessary re-renders
 */
export const StockChart = React.memo(StockChartComponent, arePropsEqual);

export default StockChart;
